import { List, Map } from "immutable";
import * as Sentry from "@sentry/browser"; // error tacking
import { MUTATE_OPERATION_ENHANCED, COLLECTIONS } from "constants/action-types";
import { OperationType } from "actions/mutate";
import { IDoorTotalDelta } from "reducers/collection";
import {
    calculationsReturnedForMatchingRows,
    calculationsFilteredReturnedForMatchingRows,
    setDoorTotalDeltaQueue,
    calcBulkDoorTotalsForMatchingRows,
} from "actions/collections/collection-budget";
import { Observable } from "rxjs";
import { buildSmartWebWorker, createWorkerFallback } from "lib/worker";
import {
    AllocationBag,
    AllocationBagType,
    AllocationDimensionIndex,
    BAG_VALUE_PARAMS_SEPARATOR,
    getAllocationBagValue,
} from "helpers/allocation-dimension";
import { parseSchema, getInOrFormula, getSkuOrId } from "helpers/product-collection";
import { getDataFromApi } from "helpers/collection";
import {
    ITEMS_NAMESPACE,
    ALLOCATION_BAG_NAMESPACE,
    Item,
    ALLOCATION_BAG_ID,
    PPS_INDICATOR,
} from "constants/product-collection";
import siteConfig from "site-config";
import { convertDeltaOperationsForFilteredTotals, mergeDoorUnitsDeltas } from "./helpers";
import { getDoors } from "reducers/selectors/collection/collection-door";
import { tableValueGetter } from "helpers/table-bag";
import {
    getAllocationDimensions,
    getAllocationDimensionValues,
    getCollectionAllocationBag,
    getCollectionItems,
} from "reducers/selectors/collection";
import {
    getDimensionFilters,
    getDoorsRelatedFields,
    shouldCalculateTotalByDelta,
    getAttributeFilters,
} from "helpers/door-collection";
import { getGridParamsFromLocation } from "components/product-collection/product-collection";
import * as SentryUtil from "lib/sentry-util";
import { AssortmentTransactions, AssortmentEvents } from "constants/sentry-metrics";
import memoizeOne from "memoize-one";
import { amphibiousGetIn } from "helpers/objUtils";
import { safeParseInt } from "lib/number";
import { getTotalConversionRate } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { updateCollectionReports } from "actions/collections";
import _ from "lodash";

const getRawAllocationBag = memoizeOne((state, collectionId) =>
    getCollectionAllocationBag(state, collectionId)?.toJS(),
);

export const calcTotalsForMatchingRowsEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.DOOR_TOTALS.CALCULATE_FOR_MATCHING_ROWS)
        .map(({ payload }) => payload)
        .debounce(() => {
            const isPerformanceOptimizationOn = store.getState().ldFlags.assortmentPerformance;
            const debounceMs = isPerformanceOptimizationOn ? 2000 : 500;
            return Observable.interval(debounceMs);
        })
        .flatMap(({ rows, doors, useAsFilteredTotals }) => {
            const state = store.getState();
            const { collection } = state;

            const collectionId = collection.collectionId;
            const {
                driveItem: { capabilities },
                totalFormulaFields: totalFormulas,
            } = collection;
            const allocationBag = getRawAllocationBag(state, collectionId);
            const dates = getAllocationDimensionValues(state, collectionId, AllocationDimensionIndex.Date) || [];
            const allocationDimensions = getAllocationDimensions(state, collectionId);

            let totalFormulaFields;

            if (!allocationDimensions.length) {
                totalFormulaFields = {
                    ...totalFormulas,
                };
            }

            const workerPayload = {
                doors,
                dates: dates.filter((date) => date.included),
                rows: rows.toJS(),
                allocationBag,
                totalFormulaFields,
            };

            const totalWorker = buildSmartWebWorker(
                createWorkerFallback(
                    `${siteConfig.assetPath || window.location.origin}/workers/product-collections/total.worker.js`,
                ),
            );

            totalWorker.process(
                workerPayload,
                (e) => {
                    const {
                        collection: { unprocessedDoorUnitsDelta },
                    } = store.getState();

                    const payloadDoorTotals = mergeDoorUnitsDeltas(e.data, unprocessedDoorUnitsDelta);

                    store.dispatch(calculationsReturnedForMatchingRows(payloadDoorTotals, useAsFilteredTotals));

                    if (capabilities?.can_edit) {
                        store.dispatch(updateCollectionReports(payloadDoorTotals));
                    }
                },
                (err) => {
                    Sentry.captureException(err);
                    // eslint-disable-next-line no-console
                    console.error(err);
                },
            );

            return [];
        });

export const calcTotalsFilteredForMatchingRowsEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.DOOR_TOTALS_FILTERED.CALCULATE_FOR_MATCHING_ROWS)
        .map(({ payload }) => payload)
        .debounce(() => {
            const isPerformanceOptimizationOn = store.getState().ldFlags.assortmentPerformance;
            const debounceMs = isPerformanceOptimizationOn ? 2000 : 500;
            return Observable.interval(debounceMs);
        })
        .flatMap(({ rows, doors }) => {
            const state = store.getState();
            const { collection } = state;
            const collectionId = collection.collectionId;
            const allocationBag = getRawAllocationBag(state, collectionId);
            const dates = getAllocationDimensionValues(state, collectionId, AllocationDimensionIndex.Date) || [];
            const {
                routing: { locationBeforeTransitions },
            } = store.getState();
            const dimensionFilters = getDimensionFilters(getGridParamsFromLocation(locationBeforeTransitions));
            const attributeFilters = getAttributeFilters(getGridParamsFromLocation(locationBeforeTransitions));
            const { totalFormulaFields: totalFormulas } = collection;
            const allocationDimensions = getAllocationDimensions(state, collectionId);
            let totalFormulaFields;

            if (!allocationDimensions.length) {
                totalFormulaFields = {
                    ...totalFormulas,
                };
            }
            const workerPayload = {
                doors,
                dates: dates.filter((date) => date.included),
                rows: rows.toJS(),
                dimensionFilters,
                attributeFilters,
                allocationBag,
                totalFormulaFields,
            };

            const totalWorker = buildSmartWebWorker(
                createWorkerFallback(
                    `${siteConfig.assetPath || window.location.origin}/workers/product-collections/total.worker.js`,
                ),
            );
            totalWorker.process(
                workerPayload,
                (e) => {
                    const {
                        collection: { unprocessedDoorUnitsDelta },
                    } = store.getState();
                    store.dispatch(
                        calculationsFilteredReturnedForMatchingRows(
                            mergeDoorUnitsDeltas(e.data, unprocessedDoorUnitsDelta),
                        ),
                    );
                },
                (err) => {
                    Sentry.captureException(err);
                    // eslint-disable-next-line no-console
                    console.error(err);
                },
            );

            return [];
        });

const findItemById = (items: List<Map<string, any>>, id: string) =>
    items?.find((item) => amphibiousGetIn(item, Item.FIELDS.ID) === id);
const getItemIdByPath = (path: string = "") => path.split(BAG_VALUE_PARAMS_SEPARATOR)[0];
const getBagItemBulkForPath = (itemId: string, path: string[], allocationBag: AllocationBag) =>
    safeParseInt(getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, path), 0);

function isAllocationPerformed(obj: any): boolean {
    const operations = _.get(obj, "operations");

    if (!Array.isArray(operations)) {
        return false;
    }

    const regex = /^(.+?)#1$/;

    for (let i = 0; i < operations.length; i++) {
        const childKey = _.get(operations[i], "childKey");
        if (typeof childKey === "string" && regex.test(childKey)) {
            return true;
        }
    }

    return false;
}
// TODO: New allocation bag. NU-23295
export const calcDoorTotalOnCellChange = (action$, store) => {
    /**
     * Filter by allocation bag specific condition.
     * - If collection is using new allocation bag (if `allocationBag` exists)
     * - Due to performance reason, we should not calculate by delta when operation group involves more than certain amount of mutations (`shouldCalculateTotalByDelta`)
     */
    const allocationBagDeltaCalculationFilter = (payload) => {
        const {
            HEAD: { object_type },
        } = payload.payload;
        const state = store.getState();
        const { collection, mutate } = state;
        const allocationBag = getCollectionAllocationBag(state, collection.collectionId);
        const ppsIndicator = payload?.payload?.value?.getIn(["fields", PPS_INDICATOR])?.toJS()?.bool_value;
        if (ppsIndicator && isAllocationPerformed(payload.payload)) {
            return false;
        }
        // Due to performance issue (https://nuorder-inc.atlassian.net/browse/NU-24938), we'll not calculate totals through delta changes.
        // Instead, we'll recalculate the whole thing in webworker to defer looping through the whole changes.
        if (allocationBag && object_type === ALLOCATION_BAG_NAMESPACE) {
            const isPerformanceOptimizationOn = store.getState().ldFlags.assortmentPerformance;
            return (
                !isPerformanceOptimizationOn &&
                shouldCalculateTotalByDelta(mutate.startingMutationCount.get(ALLOCATION_BAG_ID, 0))
            );
        }

        return true;
    };

    return action$
        .ofType(MUTATE_OPERATION_ENHANCED)
        .filter(
            ({
                payload: {
                    HEAD: { object_type },
                },
            }) => [ITEMS_NAMESPACE, ALLOCATION_BAG_NAMESPACE].includes(object_type),
        )
        .filter(allocationBagDeltaCalculationFilter)
        .map(({ payload }) => {
            const state = store.getState();
            const { collection, collectionItemDisplayPreference } = state;
            const currentAllocationBag = getCollectionAllocationBag(state, collection.collectionId);
            const doors = getDoors(state, payload.HEAD.document_id);
            const dates =
                getAllocationDimensionValues(state, payload.HEAD.document_id, AllocationDimensionIndex.Date) || [];
            const { formulaFields } = parseSchema(collectionItemDisplayPreference.currentView);
            const objectType = payload.HEAD.object_type;
            const upOperations = payload.up.operations;
            const downOperations = payload.down.operations;

            // When object type is not ALLOCATION_BAG_NAMESPACE (eg: when updating cost or retail), we will use current allocation bag.
            let upAllocationBag = currentAllocationBag;
            let downAllocationBag = currentAllocationBag;
            if (objectType === ALLOCATION_BAG_NAMESPACE) {
                upAllocationBag = payload.snapshot;
                downAllocationBag = payload.oldSnapshot;
            }

            return {
                doors,
                dates: dates.filter((date) => date.included),
                collection,
                formulaFields,
                upOperations,
                downOperations,
                upAllocationBag,
                downAllocationBag,
                items: getCollectionItems(state, collection.collectionId),
                objectType,
                state,
            };
        })
        .flatMap(
            ({
                collection,
                doors,
                upOperations = [],
                downOperations,
                formulaFields,
                dates,
                upAllocationBag,
                downAllocationBag,
                items,
                objectType,
                state,
            }) => {
                const spanTransactionName =
                    objectType === ALLOCATION_BAG_NAMESPACE
                        ? AssortmentTransactions.MutateAllocationBag
                        : AssortmentTransactions.Mutate;
                SentryUtil.startSpan(spanTransactionName, AssortmentEvents.MutateCalculateTotals);

                // A map to skip same row id
                const seenUpAllocationBagRowIds = {};
                const deltaOperationsMap: { [doorId: string]: IDoorTotalDelta } = upOperations.reduce(
                    (acc1, upOperation, upOperationIdx) => {
                        if (upAllocationBag && objectType === ALLOCATION_BAG_NAMESPACE) {
                            const [rowId, bagType] = upOperation.childKey.split(BAG_VALUE_PARAMS_SEPARATOR);
                            // We only care about bulks unit for total calculation.
                            // We also don't need to process same row more than once
                            if (bagType != AllocationBagType.bulks || seenUpAllocationBagRowIds[rowId]) {
                                return acc1;
                            }

                            seenUpAllocationBagRowIds[rowId] = true;
                        }
                        const downOperation = downOperations[upOperationIdx];
                        // Skip move operation
                        if (!filterMoveOperation(upOperation) || !filterMoveOperation(downOperation)) {
                            return acc1;
                        }

                        // Skip non door-related operation
                        if (
                            !upAllocationBag &&
                            (!filterDoorOperation(upOperation, doors) || !filterDoorOperation(downOperation, doors))
                        ) {
                            return acc1;
                        }

                        if (
                            objectType === ALLOCATION_BAG_NAMESPACE &&
                            [upOperation.operation, downOperation.operation].includes(OperationType.Remove)
                        ) {
                            upOperation.childKey = upOperation.childKey ?? downOperation.childKey;
                            downOperation.childKey = downOperation.childKey ?? upOperation.childKey;
                        }

                        const upBulks =
                            upOperation.value &&
                            (Map.isMap(upOperation.value.getIn(["bulks"]))
                                ? upOperation.value.getIn(["bulks"]).toJS()
                                : upOperation.value.getIn(["bulks"]));
                        const upItem =
                            upAllocationBag && objectType === ALLOCATION_BAG_NAMESPACE
                                ? findItemById(items, getItemIdByPath(upOperation.childKey))
                                : upOperation.value;
                        const upSkuOrId = upItem && getSkuOrId(upItem);

                        const downBulks =
                            downOperation.value &&
                            (Map.isMap(downOperation.value.getIn(["bulks"]))
                                ? downOperation.value.getIn(["bulks"]).toJS()
                                : downOperation.value.getIn(["bulks"]));
                        const downItem =
                            downAllocationBag && objectType === ALLOCATION_BAG_NAMESPACE
                                ? findItemById(items, getItemIdByPath(downOperation.childKey))
                                : downOperation.value;
                        const downSkuOrId = downItem && getSkuOrId(downItem);

                        const upId = amphibiousGetIn(upItem, Item.FIELDS.ID);
                        const downId = amphibiousGetIn(upItem, Item.FIELDS.ID);

                        // quick lookup formula
                        const getInOrFormulaFromField = (field, operation) =>
                            getInOrFormula(
                                formulaFields[field] || { path: ["fields", field, "string_value"] },
                                operation,
                            );

                        const upSeenSku = {};
                        const downSeenSku = {};
                        const totals = doors.reduce((acc2, door) => {
                            const upDoorBulkLookup = dates.map((date) => {
                                const units = upAllocationBag
                                    ? getBagItemBulkForPath(upId, [date.id, door.id], upAllocationBag)
                                    : tableValueGetter(upBulks, [date.id, door.id]) || 0;

                                return {
                                    date: date.id,
                                    units,
                                    sku: units > 0 ? upSkuOrId : undefined,
                                };
                            });

                            const downDoorBulkLookup = dates.map((date) => {
                                const units = downAllocationBag
                                    ? getBagItemBulkForPath(downId, [date.id, door.id], downAllocationBag)
                                    : tableValueGetter(downBulks, [date.id, door.id]) || 0;

                                return {
                                    date: date.id,
                                    units,
                                    sku: units > 0 ? downSkuOrId : undefined,
                                };
                            });

                            let downUnits;
                            if (dates.length > 0) {
                                downUnits = getInOrFormulaFromField(
                                    door.id,
                                    downAllocationBag ? downItem : downOperation.value,
                                );
                            } else {
                                downUnits = downAllocationBag
                                    ? getBagItemBulkForPath(downId, [door.id], downAllocationBag)
                                    : tableValueGetter(downBulks, [door.id]) || 0;
                            }
                            const downRetail = getInOrFormulaFromField(
                                door.retail.fieldName,
                                downAllocationBag ? downItem : downOperation.value,
                            );
                            const downWholesale = getInOrFormulaFromField(
                                door.wholesale.fieldName,
                                downAllocationBag ? downItem : downOperation.value,
                            );
                            const downUnitsTotal = downDoorBulkLookup.reduce((sum, bulk) => sum + bulk.units, 0);

                            let upUnits;
                            if (dates.length > 0) {
                                upUnits = getInOrFormulaFromField(
                                    door.id,
                                    upAllocationBag ? upItem : upOperation.value,
                                );
                            } else {
                                upUnits = upAllocationBag
                                    ? getBagItemBulkForPath(upId, [door.id], upAllocationBag)
                                    : tableValueGetter(upBulks, [door.id]) || 0;
                            }

                            const upRetail = getInOrFormulaFromField(
                                door.retail.fieldName,
                                upAllocationBag ? upItem : upOperation.value,
                            );
                            const upWholesale = getInOrFormulaFromField(
                                door.wholesale.fieldName,
                                upAllocationBag ? upItem : upOperation.value,
                            );
                            const upUnitsTotal = upDoorBulkLookup.reduce((sum, bulk) => sum + bulk.units, 0);

                            if (upUnits > 0 && !upSeenSku[upSkuOrId]) {
                                upSeenSku[upSkuOrId] = true;
                            }

                            if (downUnits > 0 && !downSeenSku[downSkuOrId]) {
                                downSeenSku[downSkuOrId] = true;
                            }
                            const totalConversionRate = getTotalConversionRate(door);
                            let deltaOperation = acc2[door.id];
                            deltaOperation = {
                                door: door.id,
                                classifications: door.classifications,
                                totalConversionRate,
                                downTotal: {
                                    deliveries: downDoorBulkLookup.reduce(
                                        (accb, bulk) => ({
                                            ...accb,
                                            [bulk.date]: {
                                                units:
                                                    (deltaOperation?.downTotal.deliveries[bulk.date]?.units ?? 0) +
                                                    bulk.units,
                                                retail:
                                                    (deltaOperation?.downTotal.deliveries[bulk.date]?.retail ?? 0) +
                                                    bulk.units * downRetail,
                                                wholesale:
                                                    (deltaOperation?.downTotal.deliveries[bulk.date]?.wholesale ?? 0) +
                                                    bulk.units * downWholesale,
                                                sku: accb[bulk.date] ?? bulk.sku,
                                            },
                                        }),
                                        {},
                                    ),
                                    units: (deltaOperation?.downTotal.units ?? 0) + downUnits + downUnitsTotal,
                                    retail:
                                        (deltaOperation?.downTotal.retail ?? 0) +
                                        (downUnits + downUnitsTotal) * downRetail,
                                    wholesale:
                                        (deltaOperation?.downTotal.wholesale ?? 0) +
                                        (downUnits + downUnitsTotal) * downWholesale,
                                    sku: !downSeenSku[downSkuOrId]
                                        ? (deltaOperation?.downTotal.units ?? 0) + downUnits + downUnitsTotal > 0
                                            ? downSkuOrId
                                            : undefined
                                        : downSkuOrId,
                                },
                                upTotal: {
                                    deliveries: upDoorBulkLookup.reduce(
                                        (accb, bulk) => ({
                                            ...accb,
                                            [bulk.date]: {
                                                units:
                                                    (deltaOperation?.upTotal.deliveries[bulk.date]?.units ?? 0) +
                                                    bulk.units,
                                                retail:
                                                    (deltaOperation?.upTotal.deliveries[bulk.date]?.retail ?? 0) +
                                                    bulk.units * upRetail,
                                                wholesale:
                                                    (deltaOperation?.upTotal.deliveries[bulk.date]?.wholesale ?? 0) +
                                                    bulk.units * upWholesale,
                                                sku: accb[bulk.date] ?? bulk.sku,
                                            },
                                        }),
                                        {},
                                    ),
                                    units: (deltaOperation?.upTotal.units ?? 0) + upUnits + upUnitsTotal,
                                    retail: (deltaOperation?.upTotal.retail ?? 0) + (upUnits + upUnitsTotal) * upRetail,
                                    wholesale:
                                        (deltaOperation?.upTotal.wholesale ?? 0) +
                                        (upUnits + upUnitsTotal) * upWholesale,
                                    sku: !upSeenSku[upSkuOrId]
                                        ? (deltaOperation?.upTotal.units ?? 0) + upUnits + upUnitsTotal > 0
                                            ? upSkuOrId
                                            : undefined
                                        : upSkuOrId,
                                },
                            };
                            acc2[door.id] = deltaOperation;
                            return acc2;
                        }, acc1);
                        return totals;
                    },
                    {},
                );
                SentryUtil.finishSpan(spanTransactionName, AssortmentEvents.MutateCalculateTotals);
                const deltaOperations = Object.values(deltaOperationsMap);

                const {
                    doorTotals,
                    doorTotalsFiltered,
                    unprocessedDoorUnitsDelta,
                    driveItem: { capabilities },
                    collectionId,
                } = collection;

                if (!doorTotals) {
                    return [setDoorTotalDeltaQueue(unprocessedDoorUnitsDelta.concat(deltaOperations))];
                }

                const mergedDoorUnits = mergeDoorUnitsDeltas(doorTotals, List(deltaOperations));
                const allocationDimensions = getAllocationDimensions(state, collectionId);

                const actions = [];

                if (!allocationDimensions?.length) {
                    const allItems = getDataFromApi(collection.gridApi).data;
                    actions.push(calcBulkDoorTotalsForMatchingRows(allItems, doors));
                }

                if (deltaOperations.length === 0) {
                    return actions;
                }

                actions.push(calculationsReturnedForMatchingRows(mergedDoorUnits));

                if (capabilities?.can_edit) {
                    actions.push(updateCollectionReports(mergedDoorUnits));
                }

                if (doorTotalsFiltered) {
                    const filteredDeltaOperations = convertDeltaOperationsForFilteredTotals(
                        deltaOperations,
                        doorTotalsFiltered,
                    );
                    actions.push(
                        calculationsFilteredReturnedForMatchingRows(
                            mergeDoorUnitsDeltas(doorTotalsFiltered, List(filteredDeltaOperations)),
                        ),
                    );
                }

                return actions;
            },
        );
};

// Filter out operations that are not touching door and its related fields
const filterDoorOperation = (operation, doors) => {
    const doorsRelatedFields = getDoorsRelatedFields(doors);
    if (doorsRelatedFields.length === 0) {
        return false;
    }

    return (
        operation.deltas &&
        operation.deltas.some((delta) => {
            const doorMatchRegex = new RegExp(`/fields/(${doorsRelatedFields.join("|")})|/bulks`, "i");
            return doorMatchRegex.test(delta.get("path"));
        })
    );
};

// Filter out `move` operations. It doesn't affect door calculation
const filterMoveOperation = (operation) => operation.operation !== OperationType.Move;
